import { useContext, useEffect, useRef, useState } from 'react'
import {
  IconSettings,
  IconFiles,
  IconPlus,
  IconDotsVertical,
  IconChevronRight,
  IconCircleMinus,
  IconUpload,
  IconMessages,
  IconX,
  IconPackage
} from '@tabler/icons-react'
// @ts-ignore
import { Badge, Button } from 'dsm-react-lib'
import {
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  UncontrolledTooltip
} from 'reactstrap'
import { Tooltip } from 'react-tooltip'
import { IconTrash } from '@tabler/icons-react'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { ChatbotContext } from './context/ChatbotProvider'
import { getProjectIcon, getProjectColour, ProjectFormModal, useProjectService } from './project'
import type { ProjectFormValue, ProjectThread, ProjectFile } from './project'
import { ChatInput } from './ChatInput'
import { ChatRoom } from './ChatRoom'
import { useChatService } from './services/chatService'
import { useDataboxService } from '../Teams/services/databoxService'
import { getMessageContent } from './utils/TextUtils'
import { getFileIcon } from './utils/helpers'
import EmptyContent from '../Teams/EmptyContent'
import { DropdownMenuContainer } from '../../../styles/common'
import DeleteChatModal from './modals/DeleteChatModal'
import RemoveProjectFileModal from './modals/RemoveProjectFileModal'
import AddDataboxFilesModal from './project/AddDataboxFilesModal'
import type { SelectedDataboxFile } from './project/AddDataboxFilesModal'
import { validFileInput } from './utils/constants'

// Number of threads shown before the "View more" affordance
const PROJECT_THREADS_PREVIEW_COUNT = 5

// How many recent messages to inspect when building a thread's preview snippet
const PREVIEW_MESSAGE_FETCH_LIMIT = 5

// Maximum length of the stored preview snippet (CSS truncates the visible portion)
const PREVIEW_MAX_LENGTH = 200

// Reduce markdown/assistant content to a single clean line for the preview row
const toPlainPreview = (raw: string): string => {
  if (!raw) return ''
  return raw
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1') // images/links -> their text
    .replace(/[#*_`>~]/g, ' ') // strip common markdown markers
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, PREVIEW_MAX_LENGTH)
}

// Format a thread timestamp as a short "24 Jan" style label
const formatThreadDate = (thread: ProjectThread): string => {
  const raw = thread.lastModifiedAt || thread.createdAt
  if (!raw) return ''
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

interface ProjectViewProps {
  isLoadingAgents: boolean
  onNewConversationStart: () => void
}

const ProjectView: React.FC<ProjectViewProps> = ({ isLoadingAgents, onNewConversationStart }) => {
  const {
    selectedProject,
    setSelectedProject,
    triggerProjectsRefresh,
    currentChat,
    setCurrentChat,
    setRawMessages,
    setFetchingMsgs,
    setSelectedMessage,
    projectThreadIdsRef,
    rawMessages
  } = useContext(ChatbotContext)
  const {
    getProjectById,
    updateProject,
    isUpdatingProject,
    getProjectThreads,
    isLoadingProjectThreads,
    moveThreadToProject,
    removeThreadFromProject,
    addFileToProject,
    isAddingFile,
    uploadProjectResource,
    isUploadingFile,
    isRemovingFile,
    attachDataboxFilesToProject,
    removeDataboxFileFromProject,
    getProjectDataBoxes,
    isSavingDataBoxes
  } = useProjectService()
  const { cancelAPICall, getChatMessages, deleteChat, isDeletingChat } = useChatService()
  const { getMyDataboxes, getDataboxDocumentsPaginated } = useDataboxService()
  const navigate = useNavigate()
  const documentFileRef = useRef<HTMLInputElement | null>(null)
  const addFilesInputRef = useRef<HTMLInputElement | null>(null)
  const inputFileRef = useRef<HTMLTextAreaElement | null>(null)
  const [promptText, setPromptText] = useState('')
  const [selectedTab, setSelectedTab] = useState<'chats' | 'sources'>('chats')
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [settingsProject, setSettingsProject] = useState<ProjectFormValue | null>(null)
  const [threads, setThreads] = useState<ProjectThread[]>([])
  const [showAllThreads, setShowAllThreads] = useState(false)
  // Project files attached to this project + expand/collapse state
  const [files, setFiles] = useState<ProjectFile[]>([])
  // True for the entire file-loading sequence (uploaded resources + databox
  // attachments and their name resolution), so the count doesn't flicker 0 -> N.
  const [isResolvingFiles, setIsResolvingFiles] = useState(false)
  const [isFilesExpanded, setIsFilesExpanded] = useState(false)
  // Files currently being uploaded/attached, with simulated progress for the bar
  const [uploadingFiles, setUploadingFiles] = useState<
    { tempId: string; name: string; progress: number }[]
  >([])
  // True while a file is uploading or being attached to the project
  const isFileUploadInProgress =
    isUploadingFile || isAddingFile || isSavingDataBoxes || uploadingFiles.length > 0
  // File queued for removal (drives the confirmation modal)
  const [fileToRemove, setFileToRemove] = useState<{ id: string; name: string } | null>(null)
  // Controls the "add files from a databox" picker modal
  const [isDataboxModalOpen, setIsDataboxModalOpen] = useState(false)
  // Thread queued for deletion (drives the confirmation modal)
  const [threadToDelete, setThreadToDelete] = useState<{ id: string; name: string } | null>(null)
  // Preview snippets keyed by thread id (the list API doesn't return preview text)
  const [threadPreviews, setThreadPreviews] = useState<Record<string, string>>({})
  // Tracks threads we've already requested a preview for, to avoid duplicate fetches
  const previewRequestedRef = useRef<Set<string>>(new Set())
  // Tracks the thread we have already moved into this project to avoid duplicate moves
  const movedThreadRef = useRef<string | null>(null)
  // The chat id that was already open when this project was opened. Threads created
  // before entering the project (e.g. a chat the user had open, or one they pick from
  // history) must NOT be associated — only threads started from the project composer.
  const baselineChatIdRef = useRef<string | null>(null)
  // Track if user has sent their first message (before thread ID exists)
  const [hasStartedChat, setHasStartedChat] = useState(false)

  // Build a preview snippet for a thread from its most recent assistant (or any) message
  const buildThreadPreview = async (threadId: string) => {
    try {
      const response: any = await getChatMessages(0, PREVIEW_MESSAGE_FETCH_LIMIT, threadId)
      const messages = Array.isArray(response?.messages) ? response.messages : []
      if (!messages.length) return
      const sorted = [...messages].sort(
        (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      const source = sorted.find((m: any) => m.role === 'assistant') || sorted[0]
      const { content } = getMessageContent(source?.content || '')
      const preview = toPlainPreview(content || '')
      if (preview) setThreadPreviews((prev) => ({ ...prev, [threadId]: preview }))
    } catch {
      // Preview is best-effort; the name + date still render without it
    }
  }

  // Request previews for any threads we haven't fetched yet
  const fetchPreviewsFor = (list: ProjectThread[]) => {
    list.forEach((thread) => {
      if (previewRequestedRef.current.has(thread.id)) return
      previewRequestedRef.current.add(thread.id)
      void buildThreadPreview(thread.id)
    })
  }

  const loadThreads = async (projectId: string) => {
    const list = await getProjectThreads(projectId)
    setThreads(list)
    fetchPreviewsFor(list)
  }

  const loadFiles = async (projectId: string) => {
    setIsResolvingFiles(true)
    try {
      // Every project file (including files uploaded from the device) is stored in the
      // personal-databox collection, so this endpoint is the single source of truth for
      // the project's files. Resolve each databox's resourceIds to named file rows: the
      // GET response only carries document ids, so we look up names from the databox's
      // documents and the databox name from the user's databox list.
      const databoxes = await getProjectDataBoxes(projectId)

      const boxesWithFiles = databoxes.filter((box) => (box.resourceIds?.length ?? 0) > 0)
      let databoxFiles: ProjectFile[] = []
      if (boxesWithFiles.length) {
        const databoxList = await getMyDataboxes()
        const databoxNameById = new Map<string, string>(
          (databoxList?.items ?? databoxList ?? []).map((db: any) => [
            String(db.id),
            db.name ?? 'Untitled databox'
          ])
        )

        const resolved = await Promise.all(
          boxesWithFiles.map(async (box) => {
            const docs = await getDataboxDocumentsPaginated(
              box.personalDataBoxId,
              1,
              100,
              undefined,
              'uploadTime',
              'desc'
            )
            const nameById = new Map<string, string>(
              (Array.isArray(docs) ? docs : []).map((doc: any) => [
                String(doc?.id ?? doc?.smartDocsDocumentId ?? doc?.externalId ?? ''),
                doc?.name ?? 'Untitled file'
              ])
            )
            const databoxName = databoxNameById.get(box.personalDataBoxId) ?? 'databox'
            return (box.resourceIds ?? []).map(
              (resourceId): ProjectFile => ({
                id: resourceId,
                originalFileName: nameById.get(resourceId) ?? 'Untitled file',
                source: 'databox',
                databoxId: box.personalDataBoxId,
                databoxName
              })
            )
          })
        )
        databoxFiles = resolved.flat()
      }

      setFiles(databoxFiles)
    } finally {
      setIsResolvingFiles(false)
    }
  }

  // Inject the shine keyframes used by the upload progress bars (mirrors ChatFile).
  useEffect(() => {
    if (document.getElementById('project-upload-shine')) return
    const style = document.createElement('style')
    style.id = 'project-upload-shine'
    style.textContent = `
    @keyframes projectUploadShine {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    .project-upload-shine::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
      animation: projectUploadShine 2s infinite;
    }`
    document.head.appendChild(style)
  }, [])

  // When a project is opened, fetch its threads and start a fresh conversation so the
  // composer creates a brand new thread that we can then move into this project.
  useEffect(() => {
    if (!selectedProject) return
    // Remember whatever chat was open at this moment so we never auto-associate it.
    baselineChatIdRef.current = currentChat?.id ?? null
    movedThreadRef.current = null
    previewRequestedRef.current = new Set()
    setThreadPreviews({})
    setShowAllThreads(false)
    setHasStartedChat(false)
    setCurrentChat({ id: null, title: null })
    setRawMessages([])
    setThreads([])
    setFiles([])
    loadThreads(selectedProject.id)
    loadFiles(selectedProject.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id])

  // When user sends their first message (rawMessages updated but no thread ID yet),
  // switch to ChatRoom view to show their message and the streaming response
  useEffect(() => {
    if (!selectedProject) return
    if (currentChat?.id !== null) return // Thread already exists
    if (rawMessages.length === 0) return // No message sent yet
    if (hasStartedChat) return // Already switched to chat view
    setHasStartedChat(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawMessages.length, currentChat?.id])

  // When the composer creates a new thread (currentChat.id becomes set), move it into the
  // project and switch to the conversation view. ChatHistory handles the route update.
  useEffect(() => {
    if (!selectedProject) return
    const threadId = currentChat?.id
    // No active thread yet (project just opened / reset) — nothing to associate.
    if (!threadId) return
    // Skip the chat that was already open before the project was opened; only threads
    // started from the project composer should be associated with the project.
    if (threadId === baselineChatIdRef.current) return
    if (movedThreadRef.current === threadId) return
    movedThreadRef.current = threadId
    // Flag this thread as project-owned so the sidebar doesn't optimistically add it to
    // the flat history list when its conversation title resolves. ChatHistory's own
    // "add newly initiated chat" effect reads this same map and is what actually updates
    // the URL (via a silent history update, not a react-router navigate) — see ChatHistory.tsx.
    // Do NOT also call navigate()/window.history here: Router.jsx registers
    // /chat/project/:projectId and /chat/project/:projectId/:chatId as two SEPARATE top-level
    // routes, so any react-router navigate() crossing between them fully unmounts/remounts
    // this whole page (ChatWindow/ChatHistory/ProjectView/ChatRoom) right as the response is
    // streaming — which is what previously looked like "the screen reloading" and reset the
    // sidebar's project-list scroll position.
    projectThreadIdsRef.current.set(threadId, selectedProject)
    const projectId = selectedProject.id
    void (async () => {
      await moveThreadToProject(projectId, threadId)
      triggerProjectsRefresh()
    })()
    // Keep `selectedProject` set (rather than nulling it) so this component stays mounted
    // and its already-rendered ChatRoom instance keeps streaming the response uninterrupted.
    // Previously nulling it here made ChatWindow swap to a brand new top-level ChatRoom
    // mid-response, unmounting the in-progress conversation and losing project focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat?.id])

  // If the user explicitly leaves this conversation to go back to the project's landing
  // view (e.g. the header's project chip, or re-selecting the project in the sidebar —
  // both clear currentChat before/without changing selectedProject's id), refresh the
  // threads/files list and show the landing view again instead of leaving ChatRoom mounted.
  useEffect(() => {
    if (!selectedProject) return
    if (currentChat?.id) return // still viewing (or just starting) a conversation
    if (!hasStartedChat) return // already showing the landing view
    baselineChatIdRef.current = null
    movedThreadRef.current = null
    previewRequestedRef.current = new Set()
    setThreadPreviews({})
    setHasStartedChat(false)
    setRawMessages([])
    loadThreads(selectedProject.id)
    loadFiles(selectedProject.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat?.id])

  // Load full project details (instructions + memory mode) when opening settings
  useEffect(() => {
    if (!isSettingsOpen || !selectedProject) return
    setSettingsProject(selectedProject as ProjectFormValue)
    getProjectById(selectedProject.id).then((full) => {
      if (full) setSettingsProject(full as ProjectFormValue)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSettingsOpen, selectedProject?.id])

  if (!selectedProject) return null

  // If user has started chatting, show ChatRoom instead of project landing page
  if (hasStartedChat) {
    return (
      <ChatRoom isLoadingAgents={isLoadingAgents} onNewConversationStart={onNewConversationStart} />
    )
  }

  const ProjectIcon = getProjectIcon(selectedProject.icon)

  const onThreadSelection = (threadId: string, title: string) => {
    if (threadId === currentChat?.id) return
    setFetchingMsgs(true)
    setRawMessages([])
    void cancelAPICall(false)
    setSelectedMessage(null)
    // Mark this thread as project-owned so the sidebar's flat-history effect doesn't
    // optimistically add it to the regular chat history list when its title resolves.
    projectThreadIdsRef.current.set(threadId, selectedProject)
    setCurrentChat({ id: threadId, title })
    setSelectedProject(null)
    navigate(`/chat/project/${selectedProject.id}/${threadId}`)
  }

  // Upload the picked file(s), then attach each to the project via the files endpoint.
  const onAddFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? [])
    // Reset the input so picking the same file again still fires onChange.
    event.target.value = ''
    if (!picked.length || !selectedProject) return

    // Check if adding these files would exceed the 10-file limit
    const currentFileCount = files.length + uploadingFiles.length
    const availableSlots = 10 - currentFileCount

    if (availableSlots <= 0) {
      toast.error('Maximum limit is 10 files. No more files can be uploaded in this project.')
      return
    }

    // Only upload as many files as we have slots for
    const filesToUpload = picked.slice(0, availableSlots)
    const skippedCount = picked.length - filesToUpload.length

    // Reveal the list so the in-progress rows are visible.
    setIsFilesExpanded(true)

    let uploadedCount = 0
    for (const file of filesToUpload) {
      const tempId = `${Date.now()}-${file.name}`
      // Seed the uploading row and animate progress while the request is in flight.
      setUploadingFiles((prev) => [{ tempId, name: file.name, progress: 0 }, ...prev])
      const intervalId = setInterval(() => {
        setUploadingFiles((prev) =>
          prev.map((u) =>
            u.tempId === tempId ? { ...u, progress: Math.min(u.progress + 2, 90) } : u
          )
        )
      }, 100)

      const uploadResponse: any = await uploadProjectResource(file)
      const resourceId = uploadResponse?.documentId ?? uploadResponse?.id
      clearInterval(intervalId)

      if (uploadResponse?.status?.toLowerCase?.() !== 'success' || !resourceId) {
        toast.error(uploadResponse?.message || `Failed to upload ${file.name}`)
        setUploadingFiles((prev) => prev.filter((u) => u.tempId !== tempId))
        continue
      }

      const attached = await addFileToProject(selectedProject.id, resourceId)
      if (!attached) {
        setUploadingFiles((prev) => prev.filter((u) => u.tempId !== tempId))
        continue
      }

      // Snap to 100%, then swap the uploading row for the persisted file row.
      setUploadingFiles((prev) =>
        prev.map((u) => (u.tempId === tempId ? { ...u, progress: 100 } : u))
      )
      setFiles((prev) => [
        { id: resourceId, originalFileName: file.name, createdAt: new Date().toISOString() },
        ...prev
      ])
      setUploadingFiles((prev) => prev.filter((u) => u.tempId !== tempId))
      uploadedCount++
    }

    // Reconcile from the personal-databox collection (the single source of truth) so each
    // row carries the databoxId that removal relies on. The uploaded resource ids match the
    // collection's resourceIds, so this replaces the optimistic rows without duplicating.
    if (uploadedCount > 0) {
      await loadFiles(selectedProject.id)
    }

    // Show success message with upload summary
    if (uploadedCount > 0) {
      if (skippedCount > 0) {
        toast.warning(
          `${uploadedCount} file${
            uploadedCount > 1 ? 's' : ''
          } uploaded. Maximum limit is 10 files.`
        )
      } else {
        toast.success(`${uploadedCount} file${uploadedCount > 1 ? 's' : ''} uploaded successfully.`)
      }
    }
  }

  // Detach a file from the project (soft-deletes the link; the resource itself is kept).
  const onRemoveFile = async () => {
    if (!selectedProject || !fileToRemove) return

    const target = files.find((f) => f.id === fileToRemove.id)
    // Every project file lives in the personal-databox collection, so removal always
    // detaches the document from its owning databox. databoxId is populated for every
    // row loaded from that collection.
    if (!target?.databoxId) return

    const removed = await removeDataboxFileFromProject(
      selectedProject.id,
      target.databoxId,
      fileToRemove.id
    )

    if (!removed) return

    setFiles(files.filter((f) => f.id !== fileToRemove.id))
    setFileToRemove(null)
  }

  // Attach a subset of databox documents to the project. These are references to
  // existing databox documents (no upload step) persisted via the personal-databox
  // collection, grouped per databox.
  const onAddDataboxFiles = async (selected: SelectedDataboxFile[]) => {
    if (!selectedProject || !selected.length) return

    // Check if adding these files would exceed the 10-file limit
    const currentFileCount = files.length + uploadingFiles.length
    const availableSlots = 10 - currentFileCount

    if (availableSlots <= 0) {
      toast.error('Maximum limit is 10 files. No more files can be added in this project.')
      return
    }

    // Only add as many files as we have slots for
    const filesToAdd = selected.slice(0, availableSlots)
    const skippedCount = selected.length - filesToAdd.length

    setIsFilesExpanded(true)

    // Group the chosen documents by their owning databox for the attach call.
    const byDatabox = new Map<string, string[]>()
    for (const file of filesToAdd) {
      byDatabox.set(file.databoxId, [...(byDatabox.get(file.databoxId) ?? []), file.id])
    }
    const selections = Array.from(byDatabox.entries()).map(([databoxId, resourceIds]) => ({
      databoxId,
      resourceIds
    }))

    const attached = await attachDataboxFilesToProject(selectedProject.id, selections)
    if (!attached) {
      toast.error('Failed to add files from databox')
      return
    }

    setFiles((prev) => {
      const existingIds = new Set(prev.map((f) => f.id))
      const additions = filesToAdd
        .filter((file) => !existingIds.has(file.id))
        .map((file) => ({
          id: file.id,
          originalFileName: file.name,
          source: 'databox' as const,
          databoxId: file.databoxId,
          databoxName: file.databoxName,
          createdAt: new Date().toISOString()
        }))
      return [...additions, ...prev]
    })

    // Show success message with upload summary
    if (skippedCount > 0) {
      toast.warning(
        `${filesToAdd.length} file${
          filesToAdd.length > 1 ? 's' : ''
        } added. Maximum limit is 10 files.`
      )
    } else {
      toast.success(
        `${filesToAdd.length} file${filesToAdd.length > 1 ? 's' : ''} added successfully.`
      )
    }
  }

  // Remove a chat from the project (sets the thread's project to null; chat is not deleted)
  const onRemoveThreadFromProject = async (threadId: string) => {
    if (!selectedProject) return
    const removed = await removeThreadFromProject(selectedProject.id, threadId)
    if (!removed) return
    setThreads((prev) => prev.filter((thread) => thread.id !== threadId))
    previewRequestedRef.current.delete(threadId)
    setThreadPreviews((prev) => {
      const next = { ...prev }
      delete next[threadId]
      return next
    })
    // Remove from project tracking so the chat header updates
    projectThreadIdsRef.current.delete(threadId)
    // Keep the sidebar in sync so the chat reappears under regular history
    triggerProjectsRefresh()
  }

  // Permanently delete a thread via /api/thread/:id
  const onDeleteThread = async () => {
    if (!threadToDelete) return
    const response = await deleteChat(threadToDelete.id)
    const ok = response?.ok ?? response?.status === undefined
    if (!ok) {
      toast.error('Error deleting chat')
      return
    }
    const deletedId = threadToDelete.id
    setThreads((prev) => prev.filter((thread) => thread.id !== deletedId))
    previewRequestedRef.current.delete(deletedId)
    setThreadPreviews((prev) => {
      const next = { ...prev }
      delete next[deletedId]
      return next
    })
    // Remove from project tracking so the chat header updates
    projectThreadIdsRef.current.delete(deletedId)
    setThreadToDelete(null)
    triggerProjectsRefresh()
  }

  const visibleThreads = showAllThreads ? threads : threads.slice(0, PROJECT_THREADS_PREVIEW_COUNT)

  return (
    <div
      className={`flex flex-col items-center flex-1 overflow-hidden theme-surface chat-primary`}
      data-testid="project-view">
      <div className="w-full max-w-[832px] h-full flex flex-col gap-2 px-4 py-6 overflow-hidden">
        {/* header: icon + name + settings + add files */}
        <div className="flex items-center gap-2">
          <div className="flex flex-1 items-center gap-2 min-w-0">
            <ProjectIcon
              size={40}
              stroke={1.5}
              className="flex-shrink-0"
              style={{ color: getProjectColour(selectedProject.color) }}
            />
            <h2 className="text-heading-large font-bold theme-text-heading truncate">
              {selectedProject.name}
            </h2>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              className="flex items-center justify-center w-10 h-10 !rounded-full theme-hover-surface theme-text-subheading bg-transparent border-0 cursor-pointer outline-none"
              onClick={() => setIsSettingsOpen(true)}
              aria-label="Project settings"
              data-tooltip-id="projectSettings"
              data-testid="project-settings">
              <IconSettings size={24} className="theme-icon-muted" />
            </button>
            <Tooltip
              id="projectSettings"
              place="bottom"
              className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
              Project settings
            </Tooltip>
          </div>
        </div>

        {/* composer */}
        <div className="w-full" data-testid="project-composer">
          <ChatInput
            documentFileRef={documentFileRef}
            inputFileRef={inputFileRef}
            promptText={promptText}
            setPromptText={setPromptText}
            parentContainerWidth={700}
            isProjectChat
            isLoadingAgents={isLoadingAgents}
            onNewConversationStart={onNewConversationStart}
          />
        </div>

        {/* tabs */}
        <div className="flex items-center gap-[8px]" data-testid="project-tabs">
          <Badge
            label="Chats"
            color="primary"
            type="pill"
            size="medium"
            className={`cursor-pointer px-1 font-semibold ${
              selectedTab === 'chats'
                ? 'theme-badge-filter-active'
                : 'theme-filter-badge-inactive-primary'
            }`}
            onClick={() => setSelectedTab('chats')}
            data-testid="chats-tab"
          />
          <Badge
            label={
              <span className="flex items-center gap-[8px]">
                Sources
                {isResolvingFiles ? (
                  <Spinner size="sm" className="ml-1" />
                ) : (
                  files.length > 0 && (
                    <div
                      className="flex w-[14px] h-[14px] p-[8px] items-center justify-center rounded-full text-[12px]"
                      style={{
                        color:
                          selectedTab === 'sources'
                            ? 'var(--color-primary) !important'
                            : 'var(--white) !important',
                        backgroundColor:
                          selectedTab === 'sources'
                            ? 'var(--white) !important'
                            : 'var(--brand-accent-color) !important'
                      }}
                      data-testid="sources-count-badge">
                      {files.length}
                    </div>
                  )
                )}
              </span>
            }
            color="primary"
            type="pill"
            size="medium"
            className={`cursor-pointer px-1 font-semibold ${
              selectedTab === 'sources'
                ? 'theme-badge-filter-active'
                : 'theme-filter-badge-inactive-primary'
            }`}
            onClick={() => setSelectedTab('sources')}
            data-testid="sources-tab"
          />
        </div>

        {/* sources view */}
        {selectedTab === 'sources' && (
          <div
            className="flex flex-col flex-1 min-h-0 overflow-y-auto"
            data-testid="project-sources-view">
            <UncontrolledDropdown>
              <DropdownToggle
                tag="div"
                className="inline-flex justify-center items-center"
                data-testid="add-files-button">
                <Button
                  label={isFileUploadInProgress ? 'Uploading...' : 'Add files'}
                  variant="flat"
                  className="dsm-brand-accent cursor-pointer !rounded-full !p-[5px]"
                  icon={
                    isFileUploadInProgress ? (
                      <Spinner size="sm" color="primary" />
                    ) : (
                      <IconPlus size={18} />
                    )
                  }
                  disabled={isFileUploadInProgress}
                />
              </DropdownToggle>
              <DropdownMenuContainer end>
                <DropdownItem
                  id="upload-files"
                  disabled={isFileUploadInProgress}
                  onClick={() => addFilesInputRef.current?.click()}
                  data-testid="add-files-upload-option">
                  <IconUpload size={16} className="me-1" />
                  Upload from device
                </DropdownItem>
                <UncontrolledTooltip placement="right" target="upload-files">
                  Add files (10 max) only {[...validFileInput].join(',')}
                </UncontrolledTooltip>
                <DropdownItem
                  disabled={isFileUploadInProgress}
                  onClick={() => setIsDataboxModalOpen(true)}
                  data-testid="add-files-databox-option">
                  <IconPackage size={16} className="me-1" />
                  Add from Personal databox
                </DropdownItem>
              </DropdownMenuContainer>
            </UncontrolledDropdown>
            <input
              ref={addFilesInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={onAddFiles}
              data-testid="add-files-input"
              accept={[...validFileInput].toString()}
            />
            <hr />
            {files.length === 0 && uploadingFiles.length === 0 && !isResolvingFiles ? (
              <div
                className="flex items-center justify-center px-2 py-5"
                data-testid="project-sources-empty">
                <EmptyContent
                  icon={<IconFiles size={40} stroke={1.5} color="var(--brand-accent-color)" />}
                  title="No sources added yet"
                  description="Upload sources to give Ali deeper context about your project."
                />
              </div>
            ) : (
              <div
                className="flex flex-col rounded theme-surface-muted overflow-hidden"
                data-testid="project-files-bar">
                <div className="flex items-center p-1">
                  <div className="flex items-center gap-1 theme-text-subheading">
                    <IconFiles size={22} className="flex-shrink-0 text-[var(--color-primary)]" />
                    {isResolvingFiles ? (
                      <span
                        className="flex items-center gap-1 text-body-large theme-text-subheading"
                        data-testid="project-files-counting">
                        <Spinner size="sm" color="primary" />
                        <span>Loading...</span>
                      </span>
                    ) : (
                      <span className="text-body-large">
                        <span className="font-semibold">{files.length}</span> files added
                      </span>
                    )}
                    {isFileUploadInProgress && (
                      <span
                        className="flex items-center gap-1 ml-2 text-body-default theme-text-subheading"
                        data-testid="project-files-uploading">
                        <Spinner size="sm" color="primary" />
                        <span>Uploading...</span>
                      </span>
                    )}
                  </div>
                </div>
                <div
                  id="project-files-list"
                  className="flex flex-col h-full overflow-y-auto theme-surface"
                  data-testid="project-files-list">
                  {isResolvingFiles && files.length === 0 && uploadingFiles.length === 0 ? (
                    <div className="flex items-center gap-2 px-2 py-4 theme-text-subheading text-body-default">
                      <Spinner size="sm" color="primary" />
                      <span>Loading files...</span>
                    </div>
                  ) : files.length === 0 && uploadingFiles.length === 0 ? (
                    <div className="flex items-center justify-center px-2 py-4 theme-text-subheading text-body-default">
                      No files added yet.
                    </div>
                  ) : (
                    <>
                      {uploadingFiles.map((upload) => (
                        <div
                          key={upload.tempId}
                          className="flex items-center gap-2 px-2 py-1 border-b theme-frame-divider last:border-b-0"
                          data-testid="project-file-uploading-row">
                          <span className="flex-shrink-0">{getFileIcon(upload.name)}</span>
                          <span
                            className="flex-1 text-body-default theme-text-subheading truncate"
                            title={upload.name}>
                            {upload.name}
                          </span>
                          <div className="flex items-center gap-2 w-[120px] flex-shrink-0">
                            <div className="flex-1 h-[6px] rounded-full theme-surface-muted overflow-hidden">
                              <div
                                data-testid="project-file-progress"
                                className="h-full rounded-full bg-success relative overflow-hidden project-upload-shine transition-all duration-300"
                                style={{ width: `${upload.progress}%` }}
                              />
                            </div>
                            <span className="caption-text theme-text-subheading w-[34px] text-right">
                              {upload.progress}%
                            </span>
                          </div>
                        </div>
                      ))}
                      {files.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center gap-2 px-2 py-1 border-b theme-frame-divider last:border-b-0"
                          data-testid="project-file-row">
                          <span className="flex-shrink-0">
                            {getFileIcon(file.originalFileName)}
                          </span>
                          <div className="flex flex-col flex-1 min-w-0">
                            <span
                              className="text-body-default theme-text-heading truncate"
                              title={file.originalFileName}>
                              {file.originalFileName}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="flex items-center justify-center w-8 h-8 rounded theme-hover-surface bg-transparent border-0 cursor-pointer outline-none theme-text-subheading flex-shrink-0"
                            onClick={() =>
                              setFileToRemove({ id: file.id, name: file.originalFileName })
                            }
                            aria-label={`Remove ${file.originalFileName}`}
                            data-testid="project-file-remove">
                            <IconX size={20} />
                          </button>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* chat list */}
        {selectedTab === 'chats' && (
          <div
            className="flex flex-col flex-1 min-h-0 overflow-y-auto"
            data-testid="project-chat-list">
            {isLoadingProjectThreads ? (
              <div
                className="flex items-center gap-2 px-2 py-2 theme-text-subheading text-body-default"
                data-testid="project-threads-loading">
                <Spinner size="sm" color="primary" />
                <span>Loading chats...</span>
              </div>
            ) : threads.length === 0 ? (
              <div
                className="flex items-center justify-center px-2 py-5"
                data-testid="project-threads-empty">
                <EmptyContent
                  icon={<IconMessages size={40} stroke={1.5} color="var(--brand-accent-color)" />}
                  title="No chats yet"
                  description="Start a conversation using the box above. Chats you create here stay organised within this project."
                />
              </div>
            ) : (
              <>
                {visibleThreads.map((thread) => (
                  <div
                    key={thread.id}
                    className="flex items-center gap-2 px-2 py-2 cursor-pointer theme-hover-surface border-b theme-frame-divider"
                    onClick={() => onThreadSelection(thread.id, thread.name)}
                    data-testid="project-chat-item">
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-body-large font-semibold theme-text-heading truncate">
                        {thread.name}
                      </span>
                      {(threadPreviews[thread.id] || thread.preview) && (
                        <span className="text-body-default theme-text-subheading truncate">
                          {threadPreviews[thread.id] || thread.preview}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-body-default theme-text-subheading whitespace-nowrap">
                        {formatThreadDate(thread)}
                      </span>
                      <UncontrolledDropdown>
                        <DropdownToggle
                          tag="div"
                          className=" flex items-center justify-center w-6 h-6 rounded theme-hover-surface cursor-pointer outline-none"
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          aria-label="Chat options"
                          data-testid="project-chat-item-actions">
                          <IconDotsVertical size={18} className="theme-text-subheading" />
                        </DropdownToggle>
                        <DropdownMenuContainer
                          end
                          strategy="fixed"
                          container="body"
                          className="text-[14px] ">
                          <DropdownItem
                            className="w-full theme-dropdown-accent-item"
                            onClick={(e: any) => {
                              e.stopPropagation()
                              void onRemoveThreadFromProject(thread.id)
                            }}
                            data-testid="remove-thread-from-project">
                            <div className="flex items-center space-x-2">
                              <IconCircleMinus size={20} />
                              <span>Remove from project</span>
                            </div>
                          </DropdownItem>
                          <DropdownItem
                            className="w-full theme-dropdown-danger-item"
                            onClick={(e: any) => {
                              e.stopPropagation()
                              setThreadToDelete({ id: thread.id, name: thread.name })
                            }}
                            data-testid="delete-thread">
                            <div className="flex items-center space-x-2">
                              <IconTrash size={20} />
                              <span>Delete chat</span>
                            </div>
                          </DropdownItem>
                        </DropdownMenuContainer>
                      </UncontrolledDropdown>
                    </div>
                  </div>
                ))}
                {!showAllThreads && threads.length > PROJECT_THREADS_PREVIEW_COUNT && (
                  <div className="flex justify-center py-2">
                    <Button
                      label="View more"
                      variant="outline"
                      color="primary"
                      className="dsm-brand-accent"
                      icon={<IconChevronRight size={16} />}
                      iconPosition="end"
                      onClick={() => setShowAllThreads(true)}
                      data-testid="project-view-more"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {threadToDelete && (
        <DeleteChatModal
          isOpen={!!threadToDelete}
          toggle={() => setThreadToDelete(null)}
          onDelete={onDeleteThread}
          chatTitle={threadToDelete.name}
          isDeletingChat={isDeletingChat}
        />
      )}

      {fileToRemove && (
        <RemoveProjectFileModal
          isOpen={!!fileToRemove}
          toggle={() => setFileToRemove(null)}
          onRemove={onRemoveFile}
          fileName={fileToRemove.name}
          isRemoving={isRemovingFile || isSavingDataBoxes}
        />
      )}

      <AddDataboxFilesModal
        isOpen={isDataboxModalOpen}
        toggle={() => setIsDataboxModalOpen(false)}
        existingFileIds={files.map((f) => f.id)}
        onConfirm={onAddDataboxFiles}
      />

      {isSettingsOpen && settingsProject && (
        <ProjectFormModal
          isOpen={isSettingsOpen}
          toggle={() => setIsSettingsOpen(false)}
          project={settingsProject}
          isSubmitting={isUpdatingProject}
          onSubmit={async (payload) => {
            const updated = await updateProject(selectedProject.id, payload)
            if (updated) {
              setSelectedProject({
                ...selectedProject,
                name: payload.name,
                icon: payload.icon,
                color: payload.color,
                instructions: payload.instructions,
                memoryMode: payload.memoryMode
              })
              // Keep the sidebar list in sync with the updated name/icon/colour
              triggerProjectsRefresh()
            }
            setIsSettingsOpen(false)
          }}
        />
      )}
    </div>
  )
}

export default ProjectView
